import { STORAGE_KEYS } from '@ophelos/shared/constants/storage-keys';
import { useCallback, useEffect, useMemo, useState } from 'react';

const DEFAULT_CONSENT_VALIDITY_DAYS = 183; // ≈ 6 months (EU max re-consent cadence)
const DEFAULT_COOKIE_LIFESPAN_DAYS = 395; // ≈ 13 months (EU max cookie lifespan)
const DAY_MS = 24 * 60 * 60 * 1000;

export interface CookieConsentCategory {
  key: string;
  label: string;
  essential: boolean;
  default_granted: boolean;
}

/** Shape of governance.cookie_consent from the compiled config. */
export interface CookieConsentConfig {
  enabled?: boolean;
  policy_version?: string;
  policy_url?: string;
  consent_validity_days?: number;
  cookie_lifespan_days?: number;
  categories?: CookieConsentCategory[];
}

export type ConsentByCategory = Record<string, boolean>;

/** Schema-default consent config, used until an operator-specific config is
 * delivered. Mirrors governance.schema.json → cookie_consent defaults. */
export const DEFAULT_COOKIE_CONSENT: Required<
  Pick<CookieConsentConfig, 'enabled' | 'policy_version' | 'consent_validity_days' | 'cookie_lifespan_days' | 'categories'>
> = {
  enabled: true,
  policy_version: '1',
  consent_validity_days: DEFAULT_CONSENT_VALIDITY_DAYS,
  cookie_lifespan_days: DEFAULT_COOKIE_LIFESPAN_DAYS,
  categories: [
    { key: 'essential', label: 'Strictly necessary', essential: true, default_granted: true },
    { key: 'analytics', label: 'Analytics', essential: false, default_granted: false },
    { key: 'marketing', label: 'Marketing', essential: false, default_granted: false },
    { key: 'functional', label: 'Functional', essential: false, default_granted: false },
  ],
};

type SourceApp = 'customer-portal' | 'hub';

export interface StoredConsent {
  policyVersion: string;
  categories: ConsentByCategory;
  subjectId: string;
  subjectKind: 'anonymous' | 'customer' | 'staff';
  decision: 'accept_all' | 'reject_all' | 'update';
  ts: number;
  /** True once the audit receipt reached the server. A decision taken before
   * attribution is resolvable (e.g. Hub pre-login) is stored with this false
   * and the receipt is retried when the operator/org id later appears. */
  logged: boolean;
  /** The authenticated subject ref this anonymous decision has been re-attributed
   * to. Set once, on the first sign-in after a pre-login choice, so a later
   * different account on the same browser is not falsely attributed the choice. */
  reattributedRef?: string;
}

export interface AuthenticatedSubject {
  ref: string;
  kind: 'customer' | 'staff';
}

export interface UseCookieConsentOptions {
  config?: CookieConsentConfig;
  sourceApp: SourceApp;
  /** Operator whose scope produced the config. Provide this or organizationId to
   * attribute a receipt; a receipt is logged only when one is resolvable. */
  operatorId?: string;
  /** WorkOS organization id (e.g. Hub's authenticated session). Resolved to an
   * operator server-side, so no build-time operator env is needed. */
  organizationId?: string;
  /** Authenticated subject, when known; otherwise an anonymous id is used. */
  authenticatedSubject?: AuthenticatedSubject;
  /** Core API base URL (e.g. import.meta.env.VITE_CORE_API_URL). When absent,
   * choices persist locally but are not logged to the audit table. */
  coreApiBaseUrl?: string;
  /** Returns the signed-in customer's access code (customer portal only). A
   * customer-keyed receipt is authenticated with it so the server attributes
   * the decision to the caller's own account rather than a client-supplied
   * ref. Anonymous receipts don't use it; the Hub omits it entirely. */
  getAccessCode?: () => string | null;
}

export interface UseCookieConsentResult {
  /** Whether the banner should be shown. */
  open: boolean;
  mode: 'initial' | 'manage';
  enabled: boolean;
  categories: CookieConsentCategory[];
  policyUrl?: string;
  /** Current stored grants (for pre-filling the preferences panel). */
  consent?: ConsentByCategory;
  /** True when the stored decision is currently valid (not expired or from a
   * superseded policy version) *and* grants analytics. Gate RUM tracking on this
   * rather than the raw `consent.analytics`, so a stale grant does not keep
   * tracking on until the visitor re-consents. */
  analyticsGranted: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  save: (consent: ConsentByCategory) => void;
  /** Persistent "Cookie settings" entry point — reopens in manage mode. */
  reopen: () => void;
  onOpenChange: (open: boolean) => void;
}

export interface ConsentReceiptRequest {
  url: string;
  headers: Record<string, string>;
  body: Record<string, unknown>;
}

/** Build the POST for one consent receipt, choosing the route by subject.
 *
 * Anonymous decisions go to the public route with their client-supplied ref.
 * Customer/staff decisions go to the authenticated `/me` route, which resolves
 * the account server-side from the caller's access code — so the ref is never
 * sent, and an unauthenticated caller can't forge a customer receipt (which
 * has send-time email-tracking teeth) for an arbitrary account. Returns null
 * when a customer/staff receipt has no access code to authenticate with; the
 * caller then skips it (the anonymous receipt still records the decision). */
export function buildConsentReceiptRequest(params: {
  coreApiBaseUrl: string;
  subjectRef: string;
  subjectKind: StoredConsent['subjectKind'];
  categories: ConsentByCategory;
  policyVersion: string;
  sourceApp: SourceApp;
  decision: StoredConsent['decision'];
  operatorId?: string;
  organizationId?: string;
  accessCode?: string | null;
}): ConsentReceiptRequest | null {
  const { coreApiBaseUrl, categories, policyVersion, sourceApp, decision } = params;

  if (params.subjectKind === 'anonymous') {
    return {
      url: `${coreApiBaseUrl}/api/v1/consent-records`,
      headers: { 'Content-Type': 'application/json' },
      body: {
        operator_id: params.operatorId,
        workos_organization_id: params.organizationId,
        subject_ref: params.subjectRef,
        subject_kind: params.subjectKind,
        categories,
        policy_version: policyVersion,
        source_app: sourceApp,
        decision,
      },
    };
  }

  if (!params.accessCode) return null;
  return {
    url: `${coreApiBaseUrl}/api/v1/me/consent-records`,
    headers: { 'Content-Type': 'application/json', 'X-Access-Code': params.accessCode },
    body: { categories, policy_version: policyVersion, source_app: sourceApp, decision },
  };
}

function readStored(): StoredConsent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.COOKIE_CONSENT);
    return raw ? (JSON.parse(raw) as StoredConsent) : null;
  } catch {
    return null;
  }
}

/** True when a currently-valid stored decision grants the `analytics` category.
 * Reads strictly-necessary storage directly so callers outside React (e.g. the
 * RUM bootstrap at module load) can gate on consent. A grant that is expired or
 * from a superseded policy version is treated as not-granted, so stale consent
 * never silently starts tracking before the provider can re-prompt. Validated
 * against the schema-default windows both apps deploy; the in-app provider
 * re-checks live against the operator config. Strict `=== true`. */
export function hasAnalyticsConsent(): boolean {
  const stored = readStored();
  if (
    needsReprompt(
      stored,
      DEFAULT_COOKIE_CONSENT.policy_version,
      DEFAULT_COOKIE_CONSENT.consent_validity_days,
      DEFAULT_COOKIE_CONSENT.cookie_lifespan_days,
      Date.now()
    )
  ) {
    return false;
  }
  return stored?.categories?.analytics === true;
}

function getOrCreateSubjectId(): string {
  try {
    const existing = localStorage.getItem(STORAGE_KEYS.CONSENT_SUBJECT_ID);
    if (existing) return existing;
    const id = `anon_${crypto.randomUUID().replace(/-/g, '')}`;
    localStorage.setItem(STORAGE_KEYS.CONSENT_SUBJECT_ID, id);
    return id;
  } catch {
    return `anon_${crypto.randomUUID().replace(/-/g, '')}`;
  }
}

/** True when no valid, current-version choice is stored (so we must re-prompt).
 * Both windows are measured from `stored.ts` against the *current* config, so
 * shortening `cookie_lifespan_days`/`consent_validity_days` later takes effect
 * for already-stored consents. Pure and time-injected for unit tests. */
export function needsReprompt(
  stored: StoredConsent | null,
  policyVersion: string,
  validityDays: number,
  lifespanDays: number,
  now: number
): boolean {
  if (!stored) return true;
  if (stored.policyVersion !== policyVersion) return true; // material change
  if (now - stored.ts > lifespanDays * DAY_MS) return true; // past cookie lifespan
  if (now - stored.ts > validityDays * DAY_MS) return true; // past re-consent cadence
  return false;
}

/** Build a consent map granting/denying every non-essential category uniformly;
 * essential is always granted. */
export function buildConsent(categories: CookieConsentCategory[], grant: boolean): ConsentByCategory {
  const next: ConsentByCategory = {};
  for (const c of categories) next[c.key] = c.essential ? true : grant;
  return next;
}

/** Force every essential category on, whatever the caller sent up. */
export function normaliseConsent(categories: CookieConsentCategory[], consent: ConsentByCategory): ConsentByCategory {
  const next: ConsentByCategory = { ...consent };
  for (const c of categories) if (c.essential) next[c.key] = true;
  return next;
}

/**
 * Decide whether a stored anonymous decision should be re-attributed to the
 * now-authenticated `subjectRef`, and the record to persist if so. Returns
 * `shouldPost: false` when there is nothing to re-attribute — no decision, an
 * already customer/staff-keyed decision, or one already claimed by an earlier
 * sign-in. The returned `next` stamps `reattributedRef`, so persisting it before
 * the receipt POST is what makes attribution at-most-once: a second, different
 * account signing in on the same browser then plans `shouldPost: false` and is
 * not attributed the anonymous choice.
 */
export function planReattribution(stored: StoredConsent | null, subjectRef: string): { shouldPost: boolean; next: StoredConsent | null } {
  if (stored?.subjectKind !== 'anonymous' || stored.reattributedRef) {
    return { shouldPost: false, next: null };
  }
  return { shouldPost: true, next: { ...stored, reattributedRef: subjectRef } };
}

/**
 * Cookie-consent state for Hub + Customer Portal. Persists the choice in
 * strictly-necessary storage, decides when to re-prompt (policy-version bump,
 * 6-month validity, 13-month lifespan), and logs a receipt to the Core API on
 * every decision. Feed the result into the shared CookieConsentBanner.
 */
export function useCookieConsent(options: UseCookieConsentOptions): UseCookieConsentResult {
  const { config, sourceApp, operatorId, organizationId, authenticatedSubject, coreApiBaseUrl, getAccessCode } = options;

  const enabled = config?.enabled !== false;
  const policyVersion = config?.policy_version ?? '1';
  const validityDays = config?.consent_validity_days ?? DEFAULT_CONSENT_VALIDITY_DAYS;
  const lifespanDays = config?.cookie_lifespan_days ?? DEFAULT_COOKIE_LIFESPAN_DAYS;

  const categories = useMemo<CookieConsentCategory[]>(() => config?.categories ?? [], [config?.categories]);

  const [stored, setStored] = useState<StoredConsent | null>(() => readStored());
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'initial' | 'manage'>('initial');

  // Decide whether to prompt once the config is known.
  useEffect(() => {
    if (!enabled || categories.length === 0) {
      setOpen(false);
      return;
    }
    if (needsReprompt(stored, policyVersion, validityDays, lifespanDays, Date.now())) {
      setMode('initial');
      setOpen(true);
    }
  }, [enabled, categories.length, stored, policyVersion, validityDays, lifespanDays]);

  const persistDecision = useCallback(
    (consent: ConsentByCategory, decision: 'accept_all' | 'reject_all' | 'update') => {
      const now = Date.now();
      const record: StoredConsent = {
        policyVersion,
        categories: consent,
        subjectId: authenticatedSubject?.ref ?? getOrCreateSubjectId(),
        subjectKind: authenticatedSubject?.kind ?? 'anonymous',
        decision,
        ts: now,
        logged: false,
      };
      try {
        localStorage.setItem(STORAGE_KEYS.COOKIE_CONSENT, JSON.stringify(record));
      } catch {
        // Storage unavailable (private mode) — keep the in-memory choice.
      }
      setStored(record);
      setOpen(false);
    },
    [authenticatedSubject, policyVersion]
  );

  // Send the audit receipt once attribution is resolvable, then mark it logged.
  // Runs from the effect below, so a decision made before the operator/org id is
  // known (Hub pre-login) is still logged when the id later appears, rather than
  // being lost — the stored choice stays valid and never re-prompts.
  useEffect(() => {
    if (!coreApiBaseUrl || !(operatorId || organizationId)) return;
    if (!stored || stored.logged) return;

    const req = buildConsentReceiptRequest({
      coreApiBaseUrl,
      subjectRef: stored.subjectId,
      subjectKind: stored.subjectKind,
      categories: stored.categories,
      policyVersion: stored.policyVersion,
      sourceApp,
      decision: stored.decision,
      operatorId,
      organizationId,
      accessCode: getAccessCode?.(),
    });
    if (!req) return;

    let cancelled = false;
    void fetch(req.url, { method: 'POST', headers: req.headers, body: JSON.stringify(req.body) })
      .then((res) => {
        if (cancelled || !res.ok) return;
        // A newer decision can replace this one while the POST is in flight;
        // stamp `logged` only if the stored choice is still the one we posted
        // (matched by ts), so a late receipt never clobbers a newer decision.
        const postedTs = stored.ts;
        setStored((current) => {
          if (!current || current.ts !== postedTs || current.logged) return current;
          const logged: StoredConsent = { ...current, logged: true };
          try {
            localStorage.setItem(STORAGE_KEYS.COOKIE_CONSENT, JSON.stringify(logged));
          } catch {
            // Storage unavailable — the in-memory flag still stops a re-post.
          }
          return logged;
        });
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [stored, coreApiBaseUrl, operatorId, organizationId, sourceApp, getAccessCode]);

  // Re-attribute a pre-login anonymous decision to the visitor on their first
  // sign-in: emit one extra customer-keyed receipt so per-recipient consumers
  // (e.g. email tracking) can match on the customer id. Fires at most once per
  // stored decision (reattributedRef guards it), so a *different* account signing
  // in later on the same browser is not falsely attributed the choice. The
  // original anonymous receipt is kept. Only runs where an authenticatedSubject
  // is supplied (the customer portal); Hub passes none.
  const subjectRef = authenticatedSubject?.ref;
  const subjectKind = authenticatedSubject?.kind;
  useEffect(() => {
    if (!coreApiBaseUrl || !(operatorId || organizationId)) return;
    if (!subjectRef || !subjectKind || !stored) return;

    const plan = planReattribution(stored, subjectRef);
    if (!plan.shouldPost || !plan.next) return;

    // Claim the re-attribution synchronously, before the POST is in flight, so a
    // fast account switch on the same browser (a second sign-in before this
    // request resolves) sees reattributedRef set and does not fire a receipt
    // attributing the anonymous choice to that different account.
    const next = plan.next;
    try {
      localStorage.setItem(STORAGE_KEYS.COOKIE_CONSENT, JSON.stringify(next));
    } catch {
      // Storage unavailable — the in-memory flag still stops a re-post.
    }
    setStored(next);

    const req = buildConsentReceiptRequest({
      coreApiBaseUrl,
      subjectRef,
      subjectKind,
      categories: stored.categories,
      policyVersion: stored.policyVersion,
      sourceApp,
      decision: stored.decision,
      operatorId,
      organizationId,
      accessCode: getAccessCode?.(),
    });
    if (!req) return;

    void fetch(req.url, { method: 'POST', headers: req.headers, body: JSON.stringify(req.body) }).catch(() => {});
  }, [stored, subjectRef, subjectKind, coreApiBaseUrl, operatorId, organizationId, sourceApp, getAccessCode]);

  const analyticsGranted = useMemo(
    () => !needsReprompt(stored, policyVersion, validityDays, lifespanDays, Date.now()) && stored?.categories?.analytics === true,
    [stored, policyVersion, validityDays, lifespanDays]
  );

  const acceptAll = useCallback(() => persistDecision(buildConsent(categories, true), 'accept_all'), [persistDecision, categories]);
  const rejectAll = useCallback(() => persistDecision(buildConsent(categories, false), 'reject_all'), [persistDecision, categories]);
  const save = useCallback(
    (consent: ConsentByCategory) => persistDecision(normaliseConsent(categories, consent), 'update'),
    [persistDecision, categories]
  );

  const reopen = useCallback(() => {
    setMode('manage');
    setOpen(true);
  }, []);

  const onOpenChange = useCallback((next: boolean) => {
    // Dismissing without a decision records nothing; the next load re-prompts.
    if (!next) setOpen(false);
  }, []);

  return {
    open,
    mode,
    enabled,
    categories,
    policyUrl: config?.policy_url,
    consent: stored?.categories,
    analyticsGranted,
    acceptAll,
    rejectAll,
    save,
    reopen,
    onOpenChange,
  };
}
